import { useEffect, useState } from "react";
import { Common, Icon } from "@/components";
import { Label } from "@/components/common/Label";
import { useImportOrder } from "../providers/ImportOrderContext";
import { useImportOrders } from "../services/salesOrdersService";
import { useNavigate } from "react-router-dom";
import ReactTooltip from "react-tooltip";

const ReviewImport = () => {
  const { state, dispatch, handleCancel } = useImportOrder();
  const [isImporting, setIsImporting] = useState(false);
  const importMutation = useImportOrders();
  const navigate = useNavigate();

  const hasInvalidAddresses = state.invalidAddresses.length > 0;
  const hasInvalidRoutes = state.invalidRoutes.length > 0;
  const invalidAddressesOrderCount = Array.from(
    new Set(state.invalidAddresses.map((address) => address.docNumbers).flat())
  ).length;
  const invalidRoutesOrderCount = Array.from(
    new Set(state.invalidRoutes.map((route) => route.docNumbers).flat())
  ).length;
  const invalidOrderCount = state.invalidOrderCount || 0;
  const totalOrderCount = state.recordCount || 0;
  const validOrderCount = totalOrderCount - invalidOrderCount;

  const handleImport = async () => {
    if (!state.importSessionId) return;

    setIsImporting(true);
    try {
      const importResult = await importMutation.mutateAsync(
        state.importSessionId
      );
      dispatch({
        type: "SET_IMPORT_RESULT",
        payload: {
          success: importResult.success,
          recordsImported: importResult.recordsImported,
          errors: importResult.errors,
        },
      });
      navigate("#result");
    } catch (error) {
      console.error("Import failed:", error);
      dispatch({
        type: "SET_IMPORT_RESULT",
        payload: {
          success: false,
          recordsImported: 0,
          errors: ["An unexpected error occurred during the import process."],
        },
      });
      navigate("#result");
    } finally {
      setIsImporting(false);
    }
  };

  const handleFixAddresses = () => {
    navigate("#fix-addresses");
  };

  const handleFixRoutes = () => {
    navigate("#fix-routes");
  };

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  return (
    <div className="relative">
      <div className="h-[calc(100vh-258px)]">
        <div className="flex flex-col gap-3 mb-6">
          <h3 className="text-hd5">
            Please review your order carefully before submitting it for
            processing
          </h3>

          <div className="flex items-center justify-between w-80 font-medium">
            <span className="text-neutral-50 mr-3">
              Total orders ready for uploading:
            </span>
            <Label
              color="blue"
              isStatus
              className="min-w-[75px] justify-center font-medium"
            >
              {totalOrderCount}
            </Label>
          </div>

          <div className="flex items-center justify-between w-80 font-medium">
            <span className="text-neutral-50 mr-3">Valid orders:</span>
            <Label
              color="green"
              isStatus
              className="min-w-[75px] justify-center font-medium"
            >
              {validOrderCount}
            </Label>
          </div>

          <div className="flex items-center justify-between w-80 font-medium">
            <span className="text-neutral-50 mr-3">Invalid orders:</span>
            <Label
              color="red"
              isStatus
              className="min-w-[75px] justify-center font-medium"
            >
              {invalidOrderCount}
            </Label>
          </div>
        </div>

        {(hasInvalidAddresses || hasInvalidRoutes) && (
          <div className="flex flex-col gap-5">
            <h4 className="text-hd5">Invalid Data</h4>

            {hasInvalidAddresses && (
              <div className="flex items-center">
                <div className="flex items-center w-80">
                  <span>
                    Cannot match address on map ({invalidAddressesOrderCount}{" "}
                    orders)
                  </span>
                  <span
                    className="ml-2 text-neutral-50 cursor-help"
                    data-tip={
                      "You can map any unmatched address right here on TOM and proceed with your import if there are no other errors."
                    }
                  >
                    <Icon.InfoFill className="fill-neutral-50" size="12" />
                  </span>
                </div>
                <Common.Button
                  onClick={handleFixAddresses}
                  iconRight={Icon.AngleRight}
                  variant="outline"
                  color="blue"
                  size="sm"
                  iconType="stroke"
                >
                  Fix addresses
                </Common.Button>
              </div>
            )}

            {hasInvalidRoutes && (
              <div className="flex items-center">
                <div className="flex items-center w-80">
                  <span>
                    Route name not found ({invalidRoutesOrderCount} orders)
                  </span>
                  <span
                    className="ml-2 text-neutral-50 cursor-help"
                    data-tip={
                      "You can finalize routes by choosing a default driver for each route and proceed with your import if there are no other errors."
                    }
                  >
                    <Icon.InfoFill className="fill-neutral-50" size="12" />
                  </span>
                </div>
                <Common.Button
                  onClick={handleFixRoutes}
                  variant="outline"
                  iconRight={Icon.AngleRight}
                  color="blue"
                  size="sm"
                  iconType="stroke"
                >
                  Add routes
                </Common.Button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 mt-5">
        <Common.Button
          color="transparent"
          onClick={() => handleCancel({ mode: "cancel", needConfirm: true })}
          disabled={isImporting}
        >
          Cancel
        </Common.Button>
        <Common.Button
          onClick={handleImport}
          disabled={isImporting || invalidOrderCount > 0}
        >
          {isImporting ? (
            <div className="flex items-center">
              <Icon.Loading size="20" className="mr-2" />
              <span>Importing...</span>
            </div>
          ) : (
            "Import"
          )}
        </Common.Button>
      </div>
    </div>
  );
};

export default ReviewImport;
